import { useCallback } from 'react';
import {
  useBrandPanelQuery,
  useBrandCampaignsQuery,
  useBrandCatalogQuery,
  useBrandCatalogHealthQuery,
  useBrandCustomerAnalyticsQuery,
  useBrandDailyChecklistQuery,
  useBrandLeadsQuery,
} from '../../queries/brandMetrics';
import brandMetricsApi from '../../api/brandMetrics';
import BrandView from './BrandView';

const keepPrevious = { placeholderData: (prev) => prev };

/**
 * Panel admin de una marca: fetch del período + comparación + campañas, y
 * delega el layout a BrandView. `brand` (de la lista de marcas) da la identidad
 * del header antes de que cargue el panel; después la refresca el payload.
 */
export default function BrandPanel({ brand, dateControls, compareStart, compareEnd, onBack, brandSwitcher }) {
  const brandId = brand.id;
  const { start, end } = dateControls;

  const panelQuery = useBrandPanelQuery(brandId, start, end, keepPrevious);
  const compareQuery = useBrandPanelQuery(brandId, compareStart, compareEnd, {
    enabled: Boolean(
      brandId && compareStart && compareEnd && panelQuery.data?.brand?.vertical === 'ecommerce'
    ),
  });
  const campaigns = useBrandCampaignsQuery(brandId, start, end, keepPrevious);
  const catalog = useBrandCatalogQuery(brandId, start, end, {
    enabled: Boolean(
      brandId && start && end && panelQuery.data?.brand?.vertical === 'ecommerce'
    ),
    ...keepPrevious,
  });
  const catalogHealth = useBrandCatalogHealthQuery(brandId, {
    enabled: Boolean(brandId && panelQuery.data?.brand?.vertical === 'ecommerce'),
    ...keepPrevious,
  });
  const dailyChecklist = useBrandDailyChecklistQuery(brandId, 30, keepPrevious);
  const isEcom = panelQuery.data?.brand?.vertical === 'ecommerce';
  const customerAnalytics = useBrandCustomerAnalyticsQuery(brandId, start, end, {
    enabled: Boolean(brandId && start && end && isEcom),
    ...keepPrevious,
  });
  const isLeadGen = panelQuery.data?.brand?.vertical === 'lead_gen';
  const leads = useBrandLeadsQuery(brandId, start, end, {
    enabled: Boolean(brandId && isLeadGen),
    ...keepPrevious,
  });

  const previewFetcher = useCallback(
    (creativeId) => brandMetricsApi.getBrandCreativePreview(brandId, creativeId),
    [brandId]
  );

  const priceExplorerFetcher = useCallback(
    (params) => brandMetricsApi.getBrandPriceExplorer(brandId, params),
    [brandId]
  );

  const segmentExportFetcher = useCallback(
    (segment) => brandMetricsApi.getBrandSegmentExport(brandId, segment),
    [brandId]
  );

  return (
    <BrandView
      brand={panelQuery.data?.brand ?? brand}
      panel={{
        data: panelQuery.data,
        isLoading: panelQuery.isLoading,
        isError: panelQuery.isError,
        previousSummary: compareQuery.data?.summary,
      }}
      campaigns={campaigns}
      catalog={catalog}
      catalogHealth={catalogHealth}
      customerAnalytics={isEcom ? customerAnalytics : null}
      dailyChecklist={dailyChecklist}
      leads={isLeadGen ? leads : null}
      glass
      dateControls={dateControls}
      onBack={onBack}
      brandSwitcher={brandSwitcher}
      previewFetcher={previewFetcher}
      priceExplorerFetcher={priceExplorerFetcher}
      segmentExportFetcher={segmentExportFetcher}
    />
  );
}
